import { supabase } from '../lib/supabase';

/**
 * Compliance Center Service - Integrates with Supabase database
 * Handles VAT returns, tax adjustments, compliance alerts, and filing status
 */

class ComplianceCenterService {
  constructor() {
    this.organizationId = null;
  }

  setOrganizationId(orgId) {
    this.organizationId = orgId;
  }

  // Get current quarter period for VAT calculations
  getCurrentQuarter() {
    const now = new Date();
    let year = now?.getFullYear();
    const month = now?.getMonth();
    let quarter = Math.floor(month / 3) + 1;
    return `Q${quarter} ${year}`;
  }

  // Get previous quarters for historical data
  getPreviousQuarters(count = 3) {
    const quarters = [];
    const now = new Date();
    let year = now?.getFullYear();
    let quarter = Math.floor(now?.getMonth() / 3) + 1;

    for (let i = 0; i < count; i++) {
      quarter--;
      if (quarter < 1) {
        quarter = 4;
        year--;
      }
      quarters?.push(`Q${quarter} ${year}`);
    }
    return quarters;
  }

  // Calculate VAT returns based on invoices and bills
  async calculateVATReturns() {
    try {
      if (!this.organizationId) {
        return { data: [], error: { message: 'Organization not specified' } };
      }

      const currentQuarter = this.getCurrentQuarter();
      const previousQuarters = this.getPreviousQuarters(2);
      const allQuarters = [currentQuarter, ...previousQuarters];

      const vatReturns = [];

      for (let quarter of allQuarters) {
        // Extract year and quarter number
        const [q, year] = quarter?.split(' ');
        const quarterNum = parseInt(q?.replace('Q', ''));
        
        // Calculate quarter date range
        const startMonth = (quarterNum - 1) * 3;
        const endMonth = startMonth + 2;
        const startDate = new Date(parseInt(year), startMonth, 1);
        const endDate = new Date(parseInt(year), endMonth + 1, 0);

        // Get output VAT from invoices (sales)
        const { data: invoices, error: invoiceError } = await supabase?.from('invoices')?.select('tax_amount, total_amount, status, due_date')?.eq('organization_id', this.organizationId)?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

        if (invoiceError) {
          console.error('Error fetching invoices:', invoiceError);
          continue;
        }

        // Get input VAT from bills (purchases)
        const { data: bills, error: billError } = await supabase?.from('bills')?.select('tax_amount, total_amount, status, due_date')?.eq('organization_id', this.organizationId)?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

        if (billError) {
          console.error('Error fetching bills:', billError);
          continue;
        }

        // Calculate VAT amounts
        const outputVAT = (invoices || [])?.reduce((sum, inv) => sum + (parseFloat(inv?.tax_amount) || 0), 0);
        const inputVAT = (bills || [])?.reduce((sum, bill) => sum + (parseFloat(bill?.tax_amount) || 0), 0);
        const netLiability = Math.max(0, outputVAT - inputVAT);

        // Calculate completion percentage based on data completeness
        const totalTransactions = (invoices || [])?.length + (bills || [])?.length;
        const transactionsWithTax = (invoices || [])?.filter(inv => inv?.tax_amount && parseFloat(inv?.tax_amount) > 0)?.length + 
                                   (bills || [])?.filter(bill => bill?.tax_amount && parseFloat(bill?.tax_amount) > 0)?.length;
        
        let completionPercentage = totalTransactions > 0 ? (transactionsWithTax / totalTransactions) * 100 : 0;
        
        // Identify missing items based on data analysis
        const missingItems = [];
        if (totalTransactions === 0) {
          missingItems?.push('transaction data');
        } else {
          if (transactionsWithTax < totalTransactions) {
            missingItems?.push('tax information on some transactions');
          }
          if (completionPercentage < 50) {
            missingItems?.push('proper categorization');
          }
          if (quarter === currentQuarter && completionPercentage > 0 && completionPercentage < 100) {
            missingItems?.push('final review');
          }
        }

        // Determine status based on current date and quarter
        let status = 'draft';
        let dueDate = new Date(parseInt(year), endMonth + 1, 31); // End of month after quarter

        if (quarter === currentQuarter) {
          status = netLiability > 0 && completionPercentage >= 90 ? 'ready' : 'draft';
        } else {
          // Check if overdue (more than 30 days past due date)
          const daysPastDue = Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24));
          if (daysPastDue > 30) {
            status = 'overdue';
          } else {
            status = 'filed';
            completionPercentage = 100; // Filed returns are complete
          }
        }

        vatReturns?.push({
          id: quarter?.replace(' ', '-')?.toLowerCase(),
          period: quarter,
          status,
          dueDate: dueDate?.toISOString()?.split('T')?.[0],
          netLiability: Math.round(netLiability * 100) / 100,
          inputVAT: Math.round(inputVAT * 100) / 100,
          outputVAT: Math.round(outputVAT * 100) / 100,
          invoiceCount: (invoices || [])?.length,
          billCount: (bills || [])?.length,
          completionPercentage: Math.round(completionPercentage * 100) / 100,
          missingItems: missingItems?.length > 0 ? missingItems : null
        });
      }

      return { data: vatReturns, error: null };
    } catch (error) {
      console.error('Error calculating VAT returns:', error);
      return { data: [], error: { message: 'Failed to calculate VAT returns' } };
    }
  }

  // Get compliance alerts based on tax adjustments and overdue items
  async getComplianceAlerts() {
    try {
      if (!this.organizationId) {
        return { data: [], error: { message: 'Organization not specified' } };
      }

      const alerts = [];

      // Get recent tax adjustments
      const { data: taxAdjustments, error: taxError } = await supabase?.from('tax_adjustments')?.select('*')?.eq('organization_id', this.organizationId)?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString())?.order('created_at', { ascending: false })?.limit(5);

      if (!taxError && taxAdjustments?.length > 0) {
        taxAdjustments?.forEach(adj => {
          alerts?.push({
            id: `tax-adj-${adj?.id}`,
            type: 'warning',
            title: 'Tax Adjustment Required',
            message: `${adj?.adjustment_type}: ${adj?.description}`,
            dueDate: adj?.tax_period_end,
            priority: 'medium'
          });
        });
      }

      // Check for overdue invoices
      const { data: overdueInvoices, error: overdueError } = await supabase?.from('invoices')?.select('count')?.eq('organization_id', this.organizationId)?.eq('status', 'overdue');

      if (!overdueError && overdueInvoices?.[0]?.count > 0) {
        alerts?.push({
          id: 'overdue-invoices',
          type: 'error',
          title: 'Overdue Invoices',
          message: `${overdueInvoices?.[0]?.count} invoices are overdue`,
          dueDate: null,
          priority: 'high'
        });
      }

      // Get upcoming VAT return deadlines
      const currentDate = new Date();
      const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      if (nextMonth?.getMonth() % 3 === 0) { // End of quarter
        alerts?.push({
          id: 'vat-deadline',
          type: 'deadline',
          title: 'VAT Return Due Soon',
          message: `${this.getCurrentQuarter()} VAT return due soon`,
          dueDate: nextMonth?.toISOString()?.split('T')?.[0],
          priority: 'high'
        });
      }

      return { data: alerts, error: null };
    } catch (error) {
      console.error('Error fetching compliance alerts:', error);
      return { data: [], error: { message: 'Failed to fetch compliance alerts' } };
    }
  }

  // Get transaction data for review
  async getTransactionsForReview(limit = 50) {
    try {
      if (!this.organizationId) {
        return { data: [], error: { message: 'Organization not specified' } };
      }

      const transactions = [];

      // Get recent invoices
      const { data: invoices, error: invoiceError } = await supabase?.from('invoices')?.select(`
          id,
          invoice_number,
          issue_date,
          total_amount,
          tax_amount,
          status,
          customer:customers(id, name)
        `)?.eq('organization_id', this.organizationId)?.order('issue_date', { ascending: false })?.limit(Math.floor(limit / 2));

      if (!invoiceError && invoices) {
        invoices?.forEach(inv => {
          const taxRate = inv?.total_amount > 0 ? (inv?.tax_amount / (inv?.total_amount - inv?.tax_amount)) * 100 : 0;
          transactions?.push({
            id: `inv-${inv?.id}`,
            date: inv?.issue_date,
            reference: inv?.invoice_number,
            description: `Sales invoice - ${inv?.customer?.name || 'Customer'}`,
            category: 'sales',
            netAmount: inv?.total_amount - inv?.tax_amount,
            taxRate: Math.round(taxRate * 100) / 100,
            vatAmount: inv?.tax_amount,
            status: inv?.status === 'paid' ? 'verified' : 'pending',
            type: 'invoice'
          });
        });
      }

      // Get recent bills
      const { data: bills, error: billError } = await supabase?.from('bills')?.select(`
          id,
          bill_number,
          issue_date,
          total_amount,
          tax_amount,
          status,
          vendor:vendors(id, name)
        `)?.eq('organization_id', this.organizationId)?.order('issue_date', { ascending: false })?.limit(Math.floor(limit / 2));

      if (!billError && bills) {
        bills?.forEach(bill => {
          const taxRate = bill?.total_amount > 0 ? (bill?.tax_amount / (bill?.total_amount - bill?.tax_amount)) * 100 : 0;
          transactions?.push({
            id: `bill-${bill?.id}`,
            date: bill?.issue_date,
            reference: bill?.bill_number,
            description: `Purchase - ${bill?.vendor?.name || 'Vendor'}`,
            category: 'purchases',
            netAmount: bill?.total_amount - bill?.tax_amount,
            taxRate: Math.round(taxRate * 100) / 100,
            vatAmount: bill?.tax_amount,
            status: bill?.status === 'paid' ? 'verified' : 'pending',
            type: 'bill'
          });
        });
      }

      // Sort by date
      transactions?.sort((a, b) => new Date(b.date) - new Date(a.date));

      return { data: transactions?.slice(0, limit), error: null };
    } catch (error) {
      console.error('Error fetching transactions:', error);
      return { data: [], error: { message: 'Failed to fetch transactions' } };
    }
  }

  // Get filing status from organization reminders and audit logs
  async getFilingStatus() {
    try {
      if (!this.organizationId) {
        return { data: [], error: { message: 'Organization not specified' } };
      }

      const filings = [];

      // Get VAT-related organization reminders
      const { data: reminders, error: reminderError } = await supabase?.from('organization_reminders')?.select('*')?.eq('organization_id', this.organizationId)?.eq('reminder_type', 'payment_due')?.order('created_at', { ascending: false })?.limit(10);

      if (!reminderError && reminders) {
        reminders?.forEach(reminder => {
          // Parse period from title if it contains quarter info
          const periodMatch = reminder?.title?.match(/Q[1-4]\s+\d{4}/);
          const period = periodMatch ? periodMatch?.[0] : 'Unknown Period';

          filings?.push({
            id: reminder?.id,
            period,
            status: reminder?.is_active ? 'pending' : 'accepted',
            filedDate: reminder?.last_sent_at || reminder?.created_at,
            reference: `REM-${reminder?.id?.substring(0, 8)}`,
            amount: 0, // Would need additional data to calculate
            notes: reminder?.description || 'VAT filing reminder',
            timeline: [
              {
                description: 'Reminder created',
                completed: true,
                timestamp: reminder?.created_at
              },
              {
                description: reminder?.is_active ? 'Pending action' : 'Completed',
                completed: !reminder?.is_active,
                timestamp: reminder?.last_sent_at || reminder?.updated_at
              }
            ]
          });
        });
      }

      return { data: filings, error: null };
    } catch (error) {
      console.error('Error fetching filing status:', error);
      return { data: [], error: { message: 'Failed to fetch filing status' } };
    }
  }

  // Get AI compliance insights based on data analysis
  async getAIComplianceInsights() {
    try {
      if (!this.organizationId) {
        return { data: [], error: { message: 'Organization not specified' } };
      }

      const insights = [];

      // Analyze recent tax adjustments for patterns
      const { data: recentAdjustments, error: adjError } = await supabase?.from('tax_adjustments')?.select('*')?.eq('organization_id', this.organizationId)?.gte('created_at', new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)?.toISOString())?.order('created_at', { ascending: false });

      if (!adjError && recentAdjustments?.length > 0) {
        const totalAdjustments = recentAdjustments?.reduce((sum, adj) => sum + Math.abs(parseFloat(adj?.adjustment_amount) || 0), 0);
        
        if (totalAdjustments > 1000) {
          insights?.push({
            id: 'high-adjustments',
            type: 'warning',
            priority: 'medium',
            title: 'High Tax Adjustment Activity',
            description: `${recentAdjustments?.length} tax adjustments totaling $${totalAdjustments?.toFixed(2)} in the last 90 days.`,
            details: 'Frequent tax adjustments may indicate data entry issues or process gaps.',
            recommendations: [
              'Review transaction entry procedures',
              'Implement additional validation controls',
              'Consider staff training on tax classifications'
            ],
            actionable: true,
            affectedTransactions: recentAdjustments?.slice(0, 5)?.map(adj => ({
              reference: adj?.reference_number,
              amount: adj?.adjustment_amount
            }))
          });
        }
      }

      // Check for missing tax information on recent transactions
      const { data: invoicesWithoutTax, error: invError } = await supabase?.from('invoices')?.select('count')?.eq('organization_id', this.organizationId)?.is('tax_amount', null)?.gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString());

      if (!invError && invoicesWithoutTax?.[0]?.count > 0) {
        insights?.push({
          id: 'missing-tax-info',
          type: 'error',
          priority: 'high',
          title: 'Missing Tax Information',
          description: `${invoicesWithoutTax?.[0]?.count} recent invoices are missing tax amounts.`,
          details: 'Invoices without proper tax information can lead to compliance issues and incorrect VAT filings.',
          recommendations: [
            'Review and update missing tax information',
            'Implement mandatory tax field validation',
            'Set up automatic tax calculations'
          ],
          actionable: true
        });
      }

      return { data: insights, error: null };
    } catch (error) {
      console.error('Error generating AI compliance insights:', error);
      return { data: [], error: { message: 'Failed to generate AI insights' } };
    }
  }

  // Calculate VAT summary for a specific period
  async calculateVATSummary(period = null) {
    try {
      if (!this.organizationId) {
        return { data: null, error: { message: 'Organization not specified' } };
      }

      const targetPeriod = period || this.getCurrentQuarter();
      const [q, year] = targetPeriod?.split(' ');
      const quarterNum = parseInt(q?.replace('Q', ''));
      
      const startMonth = (quarterNum - 1) * 3;
      const endMonth = startMonth + 2;
      const startDate = new Date(parseInt(year), startMonth, 1);
      const endDate = new Date(parseInt(year), endMonth + 1, 0);

      // Get output VAT (from sales)
      const { data: salesData, error: salesError } = await supabase?.from('invoices')?.select('tax_amount, total_amount')?.eq('organization_id', this.organizationId)?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

      // Get input VAT (from purchases)
      const { data: purchaseData, error: purchaseError } = await supabase?.from('bills')?.select('tax_amount, total_amount')?.eq('organization_id', this.organizationId)?.gte('issue_date', startDate?.toISOString()?.split('T')?.[0])?.lte('issue_date', endDate?.toISOString()?.split('T')?.[0]);

      if (salesError || purchaseError) {
        return { data: null, error: { message: 'Failed to calculate VAT summary' } };
      }

      const outputVAT = (salesData || [])?.reduce((sum, item) => sum + (parseFloat(item?.tax_amount) || 0), 0);
      const inputVAT = (purchaseData || [])?.reduce((sum, item) => sum + (parseFloat(item?.tax_amount) || 0), 0);
      const netLiability = Math.max(0, outputVAT - inputVAT);

      const summary = {
        period: targetPeriod,
        inputVAT: Math.round(inputVAT * 100) / 100,
        outputVAT: Math.round(outputVAT * 100) / 100,
        netLiability: Math.round(netLiability * 100) / 100,
        adjustments: 0,
        previousBalance: 0,
        totalDue: Math.round(netLiability * 100) / 100,
        salesCount: (salesData || [])?.length,
        purchaseCount: (purchaseData || [])?.length
      };

      return { data: summary, error: null };
    } catch (error) {
      console.error('Error calculating VAT summary:', error);
      return { data: null, error: { message: 'Failed to calculate VAT summary' } };
    }
  }

  // Update transaction status
  async updateTransactionStatus(transactionIds, status) {
    try {
      if (!this.organizationId || !Array.isArray(transactionIds)) {
        return { error: { message: 'Invalid parameters' } };
      }

      const results = [];

      for (const transactionId of transactionIds) {
        const [type, id] = transactionId?.split('-');
        const table = type === 'inv' ? 'invoices' : 'bills';
        
        const { error } = await supabase?.from(table)?.update({ 
            status: status === 'verified' ? 'paid' : 'pending',
            updated_at: new Date()?.toISOString()
          })?.eq('id', id)?.eq('organization_id', this.organizationId);

        if (error) {
          console.error(`Error updating ${table}:`, error);
          results?.push({ id: transactionId, success: false, error });
        } else {
          results?.push({ id: transactionId, success: true });
        }
      }

      const successCount = results?.filter(r => r?.success)?.length;
      return { 
        data: { updated: successCount, total: transactionIds?.length },
        error: null 
      };
    } catch (error) {
      console.error('Error updating transaction status:', error);
      return { error: { message: 'Failed to update transaction status' } };
    }
  }
}

// Export singleton instance
export const complianceCenterService = new ComplianceCenterService();
export default complianceCenterService;